class CS_GalleryFilter {
	rootSelector = '#gallery-453';
	filtersSelector = '.cs-button';
	galleriesSelector = '.cs-gallery';
	dragOnlySelector = '.cs-drag-scroll';
	prevSelector = '.cs-prev';
	nextSelector = '.cs-next';
	activeClass = 'cs-active';
	hiddenClass = 'cs-hidden';
	draggingClass = 'cs-dragging';

	constructor() {
		// scoped to the Featured Collections section so these selectors can't
		// accidentally pick up same-named controls from other sections (e.g. the hero slideshow's .cs-prev/.cs-next)
		this.$root = document.querySelector(this.rootSelector);
		if (!this.$root) return;

		this.$galleries = this.$root.querySelectorAll(this.galleriesSelector);
		const $filters = this.$root.querySelectorAll(this.filtersSelector);
		const $prev = this.$root.querySelector(this.prevSelector);
		const $next = this.$root.querySelector(this.nextSelector);
		const $track = this.$root.querySelector('.cs-scrollbar-track');
		const $thumb = this.$root.querySelector('.cs-scrollbar-thumb');

		this.onClick($filters[0]);

		for (const $filter of $filters) {
			$filter.addEventListener('click', () => this.onClick($filter));
		}

		$prev?.addEventListener('click', () => this.scroll(-1));
		$next?.addEventListener('click', () => this.scroll(1));

		for (const $gallery of this.$galleries) {
			this.enableDrag($gallery);
			$gallery.addEventListener('scroll', () => {
				this.updateArrows($prev, $next);
				this.updateScrollbar($track, $thumb);
			});
		}

		// standalone drag-to-scroll carousels that don't participate in filtering/arrows
		const $dragOnlyScrollers = document.querySelectorAll(`${this.dragOnlySelector}:not(${this.galleriesSelector})`);
		for (const $scroller of $dragOnlyScrollers) {
			this.enableDrag($scroller);

			const $scrollerTrack = $scroller.parentElement?.querySelector('.cs-scrollbar-track');
			const $scrollerThumb = $scroller.parentElement?.querySelector('.cs-scrollbar-thumb');

			if ($scrollerTrack && $scrollerThumb) {
				this.enableScrollbarDrag($scrollerTrack, $scrollerThumb, $scroller);
				$scroller.addEventListener('scroll', () => this.updateScrollbar($scrollerTrack, $scrollerThumb, $scroller));
				window.addEventListener('resize', () => this.updateScrollbar($scrollerTrack, $scrollerThumb, $scroller));
				this.updateScrollbar($scrollerTrack, $scrollerThumb, $scroller);
			}
		}

		if ($track && $thumb) {
			this.enableScrollbarDrag($track, $thumb);
			window.addEventListener('resize', () => this.updateScrollbar($track, $thumb));
		}

		this.updateArrows($prev, $next);
		this.updateScrollbar($track, $thumb);
	}

	get $activeGallery() {
		return this.$root?.querySelector(`${this.galleriesSelector}:not(.${this.hiddenClass})`);
	}

	onClick($filter) {
		this.filter($filter.dataset.filter);

		const { activeClass } = this;

		this.$activeFilter?.classList.remove(activeClass);
		$filter.classList.add(activeClass);

		this.$activeFilter = $filter;

		const $prev = this.$root.querySelector(this.prevSelector);
		const $next = this.$root.querySelector(this.nextSelector);
		const $track = this.$root.querySelector('.cs-scrollbar-track');
		const $thumb = this.$root.querySelector('.cs-scrollbar-thumb');
		this.updateArrows($prev, $next);
		this.updateScrollbar($track, $thumb);
	}

	filter(filter) {
		const showAll = filter == 'all';
		const { hiddenClass } = this;

		for (const $gallery of this.$galleries) {
			const show = showAll || $gallery.dataset.category == filter;
			$gallery.classList.toggle(hiddenClass, !show);
		}
	}

	scroll(direction) {
		const $gallery = this.$activeGallery;
		if (!$gallery) return;

		const $item = $gallery.querySelector('.cs-item');
		const step = $item ? $item.getBoundingClientRect().width + 20 : $gallery.clientWidth * 0.8;

		$gallery.scrollBy({ left: step * direction, behavior: 'smooth' });
	}

	updateArrows($prev, $next) {
		const $gallery = this.$activeGallery;
		if (!$gallery || (!$prev && !$next)) return;

		const maxScroll = $gallery.scrollWidth - $gallery.clientWidth;

		if ($prev) $prev.disabled = $gallery.scrollLeft <= 0;
		if ($next) $next.disabled = $gallery.scrollLeft >= maxScroll - 1;
	}

	updateScrollbar($track, $thumb, $gallery = this.$activeGallery) {
		if (!$track || !$thumb) return;

		if (!$gallery) return;

		const maxScroll = $gallery.scrollWidth - $gallery.clientWidth;

		// hides the track when every item already fits in view
		if (maxScroll <= 0) {
			$track.style.display = 'none';
			return;
		}
		$track.style.display = '';

		const thumbWidthPercent = Math.max((($gallery.clientWidth / $gallery.scrollWidth) * 100), 10);
		const maxThumbLeftPercent = 100 - thumbWidthPercent;
		const scrollRatio = $gallery.scrollLeft / maxScroll;

		$thumb.style.width = `${thumbWidthPercent}%`;
		$thumb.style.left = `${scrollRatio * maxThumbLeftPercent}%`;
	}

	enableScrollbarDrag($track, $thumb, $fixedGallery = null) {
		const { draggingClass } = this;
		let isDown = false;
		let startX = 0;
		let startScroll = 0;

		const getGallery = () => $fixedGallery ?? this.$activeGallery;

		const scrollFor = (clientX) => {
			const $gallery = getGallery();
			if (!$gallery) return;

			const trackRect = $track.getBoundingClientRect();
			const maxScroll = $gallery.scrollWidth - $gallery.clientWidth;
			const maxThumbLeft = trackRect.width - $thumb.offsetWidth;
			const targetThumbLeft = Math.min(Math.max(clientX - trackRect.left - $thumb.offsetWidth / 2, 0), maxThumbLeft);

			$gallery.scrollLeft = maxThumbLeft > 0 ? (targetThumbLeft / maxThumbLeft) * maxScroll : 0;
		};

		$thumb.addEventListener('mousedown', (e) => {
			isDown = true;
			startX = e.pageX;
			startScroll = getGallery()?.scrollLeft ?? 0;
			$thumb.classList.add(draggingClass);
			e.preventDefault();
		});

		// jumps the thumb to wherever the track itself was clicked
		$track.addEventListener('mousedown', (e) => {
			if (e.target === $thumb) return;
			scrollFor(e.clientX);
		});

		window.addEventListener('mouseup', () => {
			isDown = false;
			$thumb.classList.remove(draggingClass);
		});

		window.addEventListener('mousemove', (e) => {
			if (!isDown) return;
			const $gallery = getGallery();
			if (!$gallery) return;

			const trackRect = $track.getBoundingClientRect();
			const maxScroll = $gallery.scrollWidth - $gallery.clientWidth;
			const maxThumbLeft = trackRect.width - $thumb.offsetWidth;
			const deltaX = e.pageX - startX;
			const scrollDelta = maxThumbLeft > 0 ? (deltaX / maxThumbLeft) * maxScroll : 0;

			$gallery.scrollLeft = startScroll + scrollDelta;
		});
	}

	enableDrag($gallery) {
		const { draggingClass } = this;
		let isDown = false;
		let startX = 0;
		let startScroll = 0;
		let moved = false;

		$gallery.addEventListener('mousedown', (e) => {
			isDown = true;
			moved = false;
			startX = e.pageX;
			startScroll = $gallery.scrollLeft;
			$gallery.classList.add(draggingClass);
		});

		window.addEventListener('mouseup', () => {
			isDown = false;
			$gallery.classList.remove(draggingClass);
		});

		window.addEventListener('mousemove', (e) => {
			if (!isDown) return;
			e.preventDefault();
			const delta = e.pageX - startX;
			if (Math.abs(delta) > 5) moved = true;
			$gallery.scrollLeft = startScroll - delta;
		});

		// prevents the trailing click from firing after a drag, so links aren't accidentally opened
		$gallery.addEventListener('click', (e) => {
			if (moved) {
				e.preventDefault();
				e.stopPropagation();
				moved = false;
			}
		}, true);
	}
}

new CS_GalleryFilter();

